import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { sermons, upcomingEvents, devotional } from "@/mocks/ministry";

const socialLinks = [
  { icon: "ri-facebook-fill", label: "Facebook", href: "#", color: "bg-blue-50 text-blue-600 hover:bg-blue-600 hover:text-white" },
  { icon: "ri-instagram-line", label: "Instagram", href: "#", color: "bg-pink-50 text-pink-600 hover:bg-pink-600 hover:text-white" },
  { icon: "ri-youtube-fill", label: "YouTube", href: "#", color: "bg-red-50 text-red-600 hover:bg-red-600 hover:text-white" },
  { icon: "ri-twitter-x-line", label: "X (Twitter)", href: "#", color: "bg-stone-100 text-stone-700 hover:bg-stone-800 hover:text-white" },
  { icon: "ri-tiktok-fill", label: "TikTok", href: "#", color: "bg-stone-100 text-stone-700 hover:bg-stone-900 hover:text-white" },
  { icon: "ri-spotify-fill", label: "Spotify", href: "#", color: "bg-green-50 text-green-600 hover:bg-green-600 hover:text-white" },
];

export default function MinistryPage() {
  return (
    <main>
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://public.readdy.ai/ai/img_res/52eafc4de8f8c84c6f8e09e95796ef45.jpg')",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-stone-900/70 via-stone-800/60 to-stone-900/70" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 text-center">
          <span className="text-xs tracking-widest uppercase text-amber-300 font-semibold">Faith &amp; Community</span>
          <h1
            className="text-4xl md:text-6xl font-bold text-white mt-3 mb-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Ministry &amp; Outreach
          </h1>
          <p className="text-white/75 max-w-xl mx-auto text-base leading-relaxed">
            Rooted in faith, committed to community. Join us as we worship, grow, and serve together.
          </p>
        </div>
      </section>

      {/* Social Media */}
      <section className="py-14 bg-white border-b border-stone-100">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h2
                className="text-2xl font-bold text-stone-800"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Connect with Our Ministry
              </h2>
              <p className="text-stone-500 text-sm mt-1">Follow us for sermons, devotionals, and community updates.</p>
            </div>
            <div className="flex items-center gap-3 flex-wrap justify-center">
              {socialLinks.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  aria-label={s.label}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${s.color}`}
                >
                  <i className={`${s.icon} text-base`} />
                  {s.label}
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Recent Sermons */}
      <section className="py-20 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-end justify-between mb-10">
            <div>
              <span className="text-xs tracking-widest uppercase text-amber-700 font-semibold">Messages</span>
              <h2
                className="text-3xl font-bold text-stone-800 mt-2"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Recent Sermons
              </h2>
            </div>
            <a href="#" className="text-sm text-amber-700 font-medium flex items-center gap-1 hover:gap-2 transition-all cursor-pointer whitespace-nowrap">
              View All <i className="ri-arrow-right-line" />
            </a>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {sermons.map((sermon) => (
              <div key={sermon.id} className="bg-white rounded-2xl overflow-hidden border border-stone-100 hover:border-amber-200 transition-all group">
                <div className="relative w-full h-48 overflow-hidden">
                  <img
                    src={sermon.thumbnail}
                    alt={sermon.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-stone-900/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-white/90 text-amber-700 text-xl cursor-pointer">
                      <i className="ri-play-fill" />
                    </div>
                  </div>
                </div>
                <div className="p-5">
                  <p className="text-xs text-stone-400 mb-1">{sermon.date} &bull; {sermon.duration}</p>
                  <h3 className="font-bold text-stone-800 text-base mb-1 leading-snug">{sermon.title}</h3>
                  <p className="text-xs text-amber-700 italic mb-4">{sermon.scripture}</p>
                  <a
                    href={sermon.youtubeUrl}
                    className="flex items-center gap-2 text-sm text-amber-700 font-medium hover:gap-3 transition-all cursor-pointer"
                  >
                    <i className="ri-youtube-fill" /> Watch Sermon
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Events + Devotional */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Events */}
          <div>
            <span className="text-xs tracking-widest uppercase text-amber-700 font-semibold">Calendar</span>
            <h2
              className="text-3xl font-bold text-stone-800 mt-2 mb-8"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Upcoming Events
            </h2>
            <div className="flex flex-col gap-4">
              {upcomingEvents.map((event) => (
                <div key={event.id} className="flex items-start gap-4 p-5 rounded-xl border border-stone-100 bg-stone-50 hover:border-amber-200 transition-all">
                  <div className="flex-shrink-0 w-14 h-14 flex flex-col items-center justify-center rounded-xl bg-amber-700 text-white text-center">
                    <span className="text-xs font-bold leading-none">{event.date.split(" ")[0]}</span>
                    <span className="text-lg font-bold leading-none">{event.date.split(" ")[1].replace(",", "")}</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-stone-800 text-sm">{event.title}</h3>
                    <p className="text-xs text-stone-400 mt-0.5">{event.time} &bull; {event.location}</p>
                    <p className="text-xs text-stone-500 mt-1 leading-relaxed">{event.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Devotional */}
          <div>
            <span className="text-xs tracking-widest uppercase text-amber-700 font-semibold">Daily Word</span>
            <h2
              className="text-3xl font-bold text-stone-800 mt-2 mb-8"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Today&apos;s Devotional
            </h2>
            <div className="bg-amber-50 border border-amber-100 rounded-2xl p-8">
              <i className="ri-double-quotes-l text-amber-400 text-4xl mb-4 block" />
              <p
                className="text-xl text-stone-800 font-medium leading-relaxed mb-4 italic"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                {devotional.quote}
              </p>
              <p className="text-amber-700 text-sm font-semibold mb-6">{devotional.scripture}</p>
              <p className="text-stone-600 text-sm leading-relaxed">{devotional.reflection}</p>
              <a
                href="#"
                className="mt-6 inline-flex items-center gap-2 text-sm text-amber-700 font-medium hover:gap-3 transition-all cursor-pointer"
              >
                Read Full Devotional <i className="ri-arrow-right-line" />
              </a>
            </div>

            {/* Prayer Request */}
            <div className="mt-6 p-6 rounded-2xl bg-stone-800 text-white">
              <h3 className="font-bold text-lg mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                Submit a Prayer Request
              </h3>
              <p className="text-stone-300 text-sm mb-4">
                We believe in the power of prayer. Share your request and our prayer team will lift you up.
              </p>
              <a
                href="/contact"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-amber-700 text-white text-sm font-semibold hover:bg-amber-800 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-hand-heart-line" /> Send Prayer Request
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
